import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { CartProvider } from "@/context/CartContext";
import ScrollToTop from "@/components/ScrollToTop";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/public/Home";
import Products from "@/pages/public/Products";
import ProductDetail from "@/pages/public/ProductDetail";
import RequestQuote from "@/pages/public/RequestQuote";
import About from "@/pages/public/About";
import DeliverySupport from "@/pages/public/DeliverySupport";
import BuildYourOwnSystem from "@/pages/public/BuildYourOwnSystem";
import ReadyMadeKits from "@/pages/public/ReadyMadeKits";
import Claims from "@/pages/public/Claims";
import Contact from "@/pages/public/Contact";
import FAQ from "@/pages/public/FAQ";
import Policy from "@/pages/public/Policy";
import AccountLogin from "@/pages/public/AccountLogin";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";
import Cart from "@/pages/public/Cart";
import AccountAuthCallback from "@/pages/public/AccountAuthCallback";
import AccountCheckout from "@/pages/public/AccountCheckout";
import StripePaymentResult from "@/pages/public/StripePaymentResult";
import Payments from "@/pages/staff/Payments";
import AccountDashboard from "@/pages/public/AccountDashboard";
import NotFound from "@/pages/public/NotFound";

import StaffLogin from "@/pages/staff/StaffLogin";
import StaffLayout from "@/components/staff/StaffLayout";
import KitBuilds from "@/pages/staff/KitBuilds";
import AbandonedCarts from "@/pages/staff/AbandonedCarts";
import Stock from "@/pages/staff/Stock";
import Files from "@/pages/staff/Files";
import PriceHistory from "@/pages/staff/PriceHistory";
import Delivery from "@/pages/staff/Delivery";
import GuidesAdmin from "@/pages/staff/GuidesAdmin";
import SupportDesk from "@/pages/staff/SupportDesk";
import TechnicalSupport from "@/pages/public/TechnicalSupport";
import FaultCodes from "@/pages/public/FaultCodes";
import SupportWidget from "@/components/support/SupportWidget";
import Guides from "@/pages/public/Guides";
import GuideArticle from "@/pages/public/GuideArticle";
import ProtectedRoute from "@/components/staff/ProtectedRoute";
import Dashboard from "@/pages/staff/Dashboard";
import Enquiries from "@/pages/staff/Enquiries";
import EnquiryDetail from "@/pages/staff/EnquiryDetail";
import Team from "@/pages/staff/Team";
import ReviewsModeration from "@/pages/staff/ReviewsModeration";
import Customers from "@/pages/staff/Customers";
import ErrorBoundary from "@/components/ErrorBoundary";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CustomerAuthProvider>
          <CartProvider>
          <ScrollToTop />
          <Toaster position="top-right" richColors closeButton />
          <SupportWidget />
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/sydney-all-in-one" element={<Navigate to="/products/cygni-all-in-one" replace />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/request-a-quote" element={<RequestQuote />} />
              <Route path="/about" element={<About />} />
              <Route path="/delivery-and-support" element={<DeliverySupport />} />
              <Route path="/kits" element={<ReadyMadeKits />} />
              <Route path="/kits/:packId" element={<ReadyMadeKits />} />
              <Route path="/build-your-kit" element={<BuildYourOwnSystem />} />
              <Route path="/build-your-own-system" element={<BuildYourOwnSystem />} />
              <Route path="/claims" element={<Claims />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/support" element={<TechnicalSupport />} />
              <Route path="/support/fault-codes" element={<FaultCodes />} />
              <Route path="/guides/:slug" element={<GuideArticle />} />
              <Route path="/policies/:section" element={<Policy />} />
              <Route path="/account/login" element={<AccountLogin />} />
              <Route path="/account/forgot-password" element={<ForgotPassword />} />
              <Route path="/account/reset-password" element={<ResetPassword />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/account/callback" element={<AccountAuthCallback />} />
              <Route path="/payment/success" element={<StripePaymentResult />} />
              <Route path="/payment/cancel" element={<StripePaymentResult />} />
              <Route path="/account/checkout" element={<AccountCheckout />} />
              <Route path="/account" element={<AccountDashboard />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Staff auth */}
            <Route path="/staff/login" element={<StaffLogin />} />

            {/* Staff workspace (protected) */}
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <StaffLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/staff/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="enquiries" element={<Enquiries />} />
              <Route path="enquiries/:id" element={<EnquiryDetail />} />
              <Route path="kit-builds" element={<KitBuilds />} />
              <Route path="baskets" element={<AbandonedCarts />} />
              <Route path="payments" element={<Payments />} />
              <Route path="stock" element={<Stock />} />
              <Route path="files" element={<Files />} />
              <Route path="price-history" element={<PriceHistory />} />
              <Route path="delivery" element={<Delivery />} />
              <Route path="guides" element={<GuidesAdmin />} />
              <Route path="support" element={<SupportDesk />} />
              <Route path="reviews" element={<ReviewsModeration />} />
              <Route path="customers" element={<Customers />} />
              <Route path="team" element={<ProtectedRoute adminOnly><Team /></ProtectedRoute>} />
            </Route>
          </Routes>
          </CartProvider>
        </CustomerAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
